import {
  makeStyles,
  tokens,
  Switch,
  Text,
  Card,
} from '@fluentui/react-components';
import {
  GlobeRegular,
  PersonBoardRegular,
  ChatRegular,
  FolderRegular,
  CalculatorRegular,
} from '@fluentui/react-icons';
import { OnboardingFormData, AccessProvisioning } from '../../../hooks/useOnboardingForm';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  intro: {
    marginBottom: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
  },
  accessItem: {
    display: 'flex',
    alignItems: 'center',
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingHorizontalL,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  icon: {
    width: '40px',
    height: '40px',
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    flexShrink: 0,
  },
  content: {
    flex: 1,
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    display: 'block',
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    display: 'block',
  },
  defaultBadge: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorBrandForeground1,
    marginLeft: tokens.spacingHorizontalS,
  },
});

interface AccessProvisioningStepProps {
  formData: OnboardingFormData;
  updateFormData: <K extends keyof OnboardingFormData>(field: K, value: OnboardingFormData[K]) => void;
}

interface AccessOption {
  key: keyof AccessProvisioning;
  title: string;
  description: string;
  icon: React.ReactNode;
  iconColor: string;
  defaultValue: boolean;
}

const accessOptions: AccessOption[] = [
  {
    key: 'vpn',
    title: 'VPN Access',
    description: 'Remote access to company network',
    icon: <GlobeRegular />,
    iconColor: '#0078d4',
    defaultValue: false,
  },
  {
    key: 'hrSystem',
    title: 'HR Onboarding System',
    description: 'Access to HR self-service portal',
    icon: <PersonBoardRegular />,
    iconColor: '#107c10',
    defaultValue: false,
  },
  {
    key: 'hubSpot',
    title: 'HubSpot Access',
    description: 'CRM and marketing platform access',
    icon: <ChatRegular />,
    iconColor: '#ff7a59',
    defaultValue: false,
  },
  {
    key: 'fileServer',
    title: 'Site FileServer Access',
    description: 'Access to shared file storage',
    icon: <FolderRegular />,
    iconColor: '#8764b8',
    defaultValue: true,
  },
  {
    key: 'sage',
    title: 'SAGE Accounting',
    description: 'Access to SAGE accounting software',
    icon: <CalculatorRegular />,
    iconColor: '#00a651',
    defaultValue: false,
  },
];

export function AccessProvisioningStep({ formData, updateFormData }: AccessProvisioningStepProps) {
  const styles = useStyles();

  const handleToggle = (key: keyof AccessProvisioning) => {
    updateFormData('accessProvisioning', {
      ...formData.accessProvisioning,
      [key]: !formData.accessProvisioning[key],
    });
  };

  return (
    <div className={styles.container}>
      <Text className={styles.intro}>
        Select the systems and services the new employee will need access to.
        You can modify these settings later if needed.
      </Text>

      {accessOptions.map((option) => (
        <Card
          key={option.key}
          className={styles.accessItem}
          onClick={() => handleToggle(option.key)}
        >
          <div
            className={styles.icon}
            style={{ backgroundColor: `${option.iconColor}20`, color: option.iconColor }}
          >
            {option.icon}
          </div>
          <div className={styles.content}>
            <Text className={styles.title}>
              {option.title}
              {option.defaultValue && (
                <span className={styles.defaultBadge}>(Default)</span>
              )}
            </Text>
            <Text className={styles.description}>{option.description}</Text>
          </div>
          <Switch
            checked={formData.accessProvisioning[option.key]}
            onChange={() => handleToggle(option.key)}
          />
        </Card>
      ))}
    </div>
  );
}
